import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import keycode from 'keycode';
import { disableBodyScroll, enableBodyScroll } from '@platform-ui/body-scroll-lock';
import { layers } from '@platform-ui/constants';
import { useAnimationDisabled } from '@platform-ui/dom-utils';
import Portal from '@platform-ui/portal';
import { dataQa, pickStyle, useCallbackRef, useMergeRefs } from '@tui-react/core';
import { MetaThemeContext, useMetaThemeColor } from '../MetaThemeContext';
import { usePopupOrder } from '../hooks';
import animationStyles from '../Animations.module.css';
import styles from './Overlay.module.css';
const DEFAULT_OVERLAY_COLOR = 'rgba(0,0,0,0.75)';
const Overlay = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    blur,
    children,
    dataQaData,
    dataQaType = 'uikit/popup.overlay',
    disableAnimation,
    disableScroll,
    focusOnClose,
    gapFillColor = DEFAULT_OVERLAY_COLOR,
    height,
    minWidth,
    onClose,
    onCloseEnd,
    onKeyDown,
    onKeyUp,
    opened,
    overflowTarget,
    popupClosedClass = animationStyles.fade_close,
    popupOpenedClass = animationStyles.fade_open,
    transparent,
    vars,
    zIndex
  } = props;
  /**
   * Флаг mounted предназначен для показа/скрытия компонента.
   * Проп opened не используется напрямую, чтобы была возможность
   * скрывать компонент после завершения анимации.
   */
  const [mounted, setMounted] = useState(opened);
  /**
   * lastActiveElRef предназначена для хранения элемента,
   * на котором был фокус до открытия Overlay, чтобы вернуть туда фокус после закрытия.
   */
  const lastActiveElRef = useRef(null);
  /**
   * clickedElRef используется для хранения элемента,
   * на котором была нажата кнопка мыши. UIKIT-3700
   */
  const clickedElRef = useRef(null);
  const scrollableWrapperRef = useRef(null);
  const popupWrapperRef = useRef(null);
  const [disableScrollOnAnimation, setDisableScrollOnAnimation] = useState(disableScroll);
  const handleInnerAnimationStart = useCallback(() => {
    const scrollEl = scrollableWrapperRef.current;
    const popupEl = popupWrapperRef.current;
    if (scrollEl && popupEl) {
      const hasScroll = popupEl.offsetHeight > scrollEl.clientHeight;
      !hasScroll && !disableScroll && setDisableScrollOnAnimation(true);
    }
  }, [disableScroll]);
  const handleInnerAnimationEnd = useCallback(() => {
    !disableScroll && setDisableScrollOnAnimation(false);
  }, [disableScroll]);
  const [backdropRef, animationDisabled] = useAnimationDisabled({
    property: 'animation',
    disabled: disableAnimation
  });
  const rootRef = useMergeRefs([forwardedRef, useCallbackRef(null, (root, prevRoot) => {
    if (root) {
      if (!root.querySelector('*:focus')) {
        var _scrollableWrapperRef;
        lastActiveElRef.current = focusOnClose || document.activeElement;
        children && ((_scrollableWrapperRef = scrollableWrapperRef.current) == null ? void 0 : _scrollableWrapperRef.focus());
      }
      disableBodyScroll(root, {
        allowTouchMove: true,
        reserveScrollBarGap: true,
        overflowTarget
      });
    } else if (prevRoot) {
      if (lastActiveElRef.current) {
        lastActiveElRef.current.focus();
        lastActiveElRef.current = null;
      }
      enableBodyScroll(prevRoot);
    }
  })]);

  /**
   * https://jira.tcsbank.ru/browse/UIKIT-6581
   */
  const {
    isPopupContextDefined,
    isPopupLast,
    popupsLength,
    prevPopupsLength
  } = usePopupOrder({
    opened,
    popupRef: rootRef
  });
  const handleKeyDown = React.useCallback(event => {
    if (keycode(event.nativeEvent) === 'esc') {
      onClose(event);
    }
    onKeyDown && onKeyDown(event);
  }, [onClose, onKeyDown]);
  const handleMouseDown = React.useCallback(event => {
    clickedElRef.current = event.target;
  }, []);
  const handleMouseUp = React.useCallback(event => {
    if (clickedElRef.current && clickedElRef.current === popupWrapperRef.current) {
      onClose(event);
      clickedElRef.current = null;
    }
  }, [onClose]);
  const handleAnimationEnd = React.useCallback(() => {
    setMounted(false);
    onCloseEnd && onCloseEnd();
  }, [onCloseEnd]);
  const handleFocusOnBeforeElement = React.useCallback(event => {
    event.preventDefault();
    if (popupWrapperRef.current) {
      const focusable = popupWrapperRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const lastFocusable = focusable[focusable.length - 1];
      lastFocusable && lastFocusable.focus();
    }
  }, []);
  const handleFocusOnAfterElement = React.useCallback(event => {
    var _scrollableWrapperRef2;
    event.preventDefault();
    (_scrollableWrapperRef2 = scrollableWrapperRef.current) == null ? void 0 : _scrollableWrapperRef2.focus();
  }, []);
  useEffect(() => {
    if (opened) {
      setMounted(true);
    }
    if (!opened && animationDisabled) {
      handleAnimationEnd();
    }
  }, [animationDisabled, handleAnimationEnd, opened]);
  const backdropStyle = transparent ? undefined : {
    boxShadow: `0 100vh 0 100vh ${gapFillColor}`
  };
  const {
    colorMixer,
    enabled
  } = useContext(MetaThemeContext);
  const shouldUpdate = !transparent && enabled;
  useMetaThemeColor({
    opened,
    overlayColor: DEFAULT_OVERLAY_COLOR,
    enabled: shouldUpdate,
    colorMixer
  });
  return mounted ? /*#__PURE__*/React.createElement(Portal, {
    to: "body"
  }, /*#__PURE__*/React.createElement("div", Object.assign({}, dataQa({
    dataQaType,
    dataQaData
  }), {
    ref: rootRef,
    role: "dialog",
    onKeyDown: handleKeyDown,
    onKeyUp: onKeyUp
  }, pickStyle(props, 'root', {
    className: cn({
      [styles.root]: true,
      [styles.root_closed]: !opened,
      [styles.root_opened]: opened,
      [styles.root_disabledScroll]: disableScroll || disableScrollOnAnimation,
      [styles.root_disabledAnimation]: disableAnimation,
      [styles.root_transparent]: transparent,
      [styles.root_blurred]: blur
    }),
    style: _extends({
      zIndex: typeof zIndex === 'number' || typeof zIndex === 'undefined' ? zIndex : layers[zIndex],
      height
    }, vars == null ? void 0 : vars(props))
  })), /*#__PURE__*/React.createElement("div", Object.assign({
    ref: backdropRef,
    onAnimationEnd: !opened && !animationDisabled ? handleAnimationEnd : undefined
  }, pickStyle(props, 'backdrop', {
    className: cn({
      [styles.backdrop]: true,
      [styles.backdrop_transparent]: transparent,
      [styles.backdrop_noAnimation]: isPopupContextDefined && (popupsLength > 1 || popupsLength === 1 && prevPopupsLength === 2)
    }),
    style: _extends({}, backdropStyle, {
      backgroundColor: isPopupLast ? undefined : 'rgb(0, 0, 0, 0)'
    })
  }))), /*#__PURE__*/React.createElement("div", Object.assign({
    tabIndex: 0,
    onFocus: handleFocusOnBeforeElement
  }, pickStyle(props, 'beforeWrapper'))), /*#__PURE__*/React.createElement("div", Object.assign({
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    tabIndex: 0,
    ref: scrollableWrapperRef
  }, pickStyle(props, 'scrollableWrapper', {
    className: styles.popupScrollableWrapper
  })), /*#__PURE__*/React.createElement("div", Object.assign({
    ref: popupWrapperRef,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    "data-qa-type": "uikit/popup.wrapper",
    onAnimationStart: handleInnerAnimationStart,
    onAnimationEnd: handleInnerAnimationEnd
  }, pickStyle(props, 'wrapper', {
    className: cn({
      [styles.popupWrapper]: true,
      [popupOpenedClass]: !disableAnimation && opened,
      [popupClosedClass]: !disableAnimation && !opened
    }),
    style: {
      minWidth
    }
  })), children)), /*#__PURE__*/React.createElement("div", Object.assign({
    tabIndex: 0,
    onFocus: handleFocusOnAfterElement
  }, pickStyle(props, 'afterWrapper'))))) : null;
});
export default Overlay;